<template>
	<div class="toast-container" v-show="notifications.length > 0">
		<transition-group name="toast" tag="div" class="toast-list">
			<Alert
				v-for="notification in notifications"
				:key="notification.id"
				:type="notification.type"
				:title="notification.title"
				:message="notification.message"
				:id="notification.id"
			/>
		</transition-group>
	</div>
</template>

<script>
export default {
	components: {
		Alert: () => import('./alert'),
	},
	created() {
		//Logic to push the most recent notification to the top of the notifications
		Event.$on('notifications:show', async (payload) => {
			let id = Math.round(Math.random() * 10000000);

			payload.id = id;

			if (
				payload.message.length > 0 &&
				!this.isMessageDisplayed(payload.message)
			) {
				this.notifications.unshift(payload); // Add to top instead of push
				await utils.sleep(payload.duration || 50);
				this.closeNotification(id);
			}
		});

		Event.$on('notification:close', (payload) => {
			this.closeNotification(payload.id);
		});
	},
	data() {
		return {
			notifications: [],
		};
	},
	methods: {
		isMessageDisplayed(message) {
			if (
				this.notifications.length > 0 &&
				this.notifications[0].message === message
			) {
				return true;
			} else {
				return false;
			}
		},
		closeNotification(id) {
			for (let i = 0; i < this.notifications.length; i++) {
				const notification = this.notifications[i];

				if (notification.id === id) {
					this.notifications.splice(i, 1);
					break;
				}
			}
		},
	},
};
</script>

<style lang="less">
.toast-container {
	position: fixed;
	top: 24px;
	right: 24px;
	z-index: 9999;
	width: 420px;
	max-height: calc(100vh - 48px);
	pointer-events: none;

	// Enable pointer events only on notifications
	.toast-notification {
		pointer-events: all;
	}
}

.toast-list {
	display: flex;
	flex-direction: column;
	gap: 0;
}

// Transition animations for Vue transition-group
.toast-enter-active {
	transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.toast-leave-active {
	transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.toast-enter-from {
	opacity: 0;
	transform: translateX(100%) scale(0.95);
}

.toast-leave-to {
	opacity: 0;
	transform: translateX(100%) scale(0.95);
}

.toast-move {
	transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

// Responsive design
@media (max-width: 768px) {
	.toast-container {
		top: 16px;
		right: 16px;
		left: 16px;
		width: auto;
		max-width: none;
	}
}

@media (max-width: 480px) {
	.toast-container {
		top: 12px;
		right: 12px;
		left: 12px;
	}
}

// Stacking effect for multiple notifications
.toast-list .toast-notification {
	&:nth-child(1) {
		z-index: 10;
		transform: scale(1) translateY(0);
	}

	&:nth-child(2) {
		z-index: 9;
		transform: scale(0.98) translateY(-4px);
		opacity: 0.95;
	}

	&:nth-child(3) {
		z-index: 8;
		transform: scale(0.96) translateY(-8px);
		opacity: 0.9;
	}

	&:nth-child(4) {
		z-index: 7;
		transform: scale(0.94) translateY(-12px);
		opacity: 0.85;
	}

	&:nth-child(n + 5) {
		display: none; // Hide notifications beyond 4
	}
}

// Smooth height transitions
.toast-container {
	overflow: visible;

	.toast-list {
		overflow: visible;
	}
}

// Accessibility improvements
@media (prefers-reduced-motion: reduce) {
	.toast-enter-active,
	.toast-leave-active,
	.toast-move {
		transition-duration: 0.1s;
	}

	.toast-notification {
		animation-duration: 0.1s;

		&:hover {
			transform: none;
		}
	}

	.toast-progress {
		animation: none;
	}

	.toast-close {
		&:hover {
			transform: none;
		}

		&:active {
			transform: none;
		}
	}
}

// High contrast mode support
@media (prefers-contrast: high) {
	.toast-notification {
		border-width: 2px;
		border-left-width: 6px;

		&.toast-success {
			border-color: #10b981;
		}

		&.toast-error {
			border-color: #ef4444;
		}

		&.toast-warning {
			border-color: #f59e0b;
		}

		&.toast-info {
			border-color: #3b82f6;
		}
	}
}
</style>
